import React from "react";

import Button from "@replo/design-system/components/button/Button";

interface OnboardingTemplateProps {
  styleLibrary?: any;
  extractedImages?: string[];
}

const getColorBrightness = (hexColor: string): number => {
  const hex = hexColor.replace("#", "");
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000;
};

const OnboardingTemplate: React.FC<OnboardingTemplateProps> = ({
  styleLibrary,
  extractedImages,
}) => {
  const getImageAtIndex = (index: number) => {
    if (!extractedImages || extractedImages.length === 0) {
      return null;
    }
    return extractedImages[index % extractedImages.length];
  };
  const headingFont = styleLibrary?.typography?.h1?.name ?? "Aeonik";
  const bodyFont = styleLibrary?.typography?.body?.name ?? "Inter";

  const primaryColor = styleLibrary?.colors?.brand?.primary?.color ?? "#000000";
  const brightness = getColorBrightness(primaryColor);
  const shouldUseWhiteLogos = brightness < 128;
  const logoFilter = shouldUseWhiteLogos
    ? "brightness(0) invert(1)"
    : "brightness(0)";

  // NOTE: (Berkley, 01/08/2025): This is a helper function to get the contrasting text color based on the background color.
  const getContrastingTextColor = (backgroundColor: string) => {
    const bgBrightness = getColorBrightness(backgroundColor);
    return bgBrightness < 128
      ? styleLibrary?.colors?.neutrals?.light ?? "#FFFFFF"
      : styleLibrary?.colors?.neutrals?.dark ?? "#000000";
  };

  return (
    <div
      className="h-full p-4 sm:p-8 md:p-12 flex justify-center items-start"
      style={{
        backgroundColor: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
      }}
    >
      <div className="flex flex-col w-full max-w-xs sm:max-w-md md:max-w-xl h-full">
        <div
          className="flex flex-col sm:flex-row p-3 sm:p-5 gap-3 sm:gap-5"
          style={{
            backgroundColor:
              styleLibrary?.colors?.neutrals?.subtle ?? "#FFFFFF",
          }}
        >
          <div className="flex flex-col justify-center gap-2 sm:gap-4 p-2 sm:p-3 flex-1">
            <div className="flex flex-col gap-2 sm:gap-3">
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                <div
                  className="text-xs font-bold"
                  style={{
                    color:
                      styleLibrary?.colors?.brand?.primary?.color ?? "#000000",
                    fontFamily: `'${bodyFont}', sans-serif`,
                  }}
                >
                  ★★★★★
                </div>
                <div
                  className="text-xs font-light"
                  style={{
                    color: styleLibrary?.colors?.neutrals?.dark ?? "#999999",
                    fontFamily: `'${bodyFont}', sans-serif`,
                  }}
                >
                  4.8 88,888+ Trusted Customer Reviews
                </div>
              </div>
              <h1
                className="text-xl sm:text-2xl md:text-3xl font-bold"
                style={{
                  color: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                  fontFamily: `'${headingFont}', sans-serif`,
                }}
              >
                Sell <span className="italic">Anything</span>
              </h1>
              <div
                className="text-xs font-light"
                style={{
                  color: styleLibrary?.colors?.neutrals?.dark ?? "#999999",
                  fontFamily: `'${bodyFont}', sans-serif`,
                }}
              >
                This is a short description that provides context for this
                section. Keep it brief and informative.
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                variant="primary"
                size="sm"
                UNSAFE_className="rounded-none w-full sm:w-[120px] h-[25px] font-light text-xs"
                style={{
                  backgroundColor:
                    styleLibrary?.colors?.brand?.primary?.color ?? "#000000",
                  color: getContrastingTextColor(
                    styleLibrary?.colors?.brand?.primary?.color ?? "#000000",
                  ),
                }}
              >
                Button
              </Button>
              <Button
                variant="secondary"
                size="sm"
                UNSAFE_className="rounded-none w-full sm:w-[120px] h-[25px] font-light text-xs"
                style={{
                  backgroundColor:
                    styleLibrary?.colors?.brand?.secondary?.color ?? "#000000",
                  color: getContrastingTextColor(
                    styleLibrary?.colors?.brand?.secondary?.color ?? "#000000",
                  ),
                }}
              >
                Button
              </Button>
            </div>
          </div>
          <div
            className="h-32 sm:h-40 md:h-50 w-full sm:w-32 md:w-50 border flex-shrink-0"
            style={{
              backgroundColor:
                styleLibrary?.colors?.neutrals?.light ?? "#999999",
              borderColor: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
              ...(getImageAtIndex(0) && {
                backgroundImage: `url(${getImageAtIndex(0)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }),
            }}
          />
        </div>
        <div
          className="flex flex-row h-16 sm:h-20 w-full gap-2 sm:gap-5 items-center justify-center px-2 sm:px-8 overflow-hidden"
          style={{
            backgroundColor:
              styleLibrary?.colors?.brand?.primary?.color ?? "#000000",
          }}
        >
          <img
            src="/images/onboarding/forbes.svg"
            alt="Forbes"
            className="px-1 sm:px-6 h-2 sm:h-3 object-contain flex-shrink-0"
            style={{ filter: logoFilter }}
          />
          <img
            src="/images/onboarding/business-insider.svg"
            alt="Business Insider"
            className="px-1 sm:px-6 h-2 sm:h-3 object-contain flex-shrink-0"
            style={{ filter: logoFilter }}
          />
          <img
            src="/images/onboarding/entrepeneur.svg"
            alt="Entrepreneur"
            className="px-1 sm:px-6 h-2 sm:h-3 object-contain flex-shrink-0"
            style={{ filter: logoFilter }}
          />
          <img
            src="/images/onboarding/newyorktimes.svg"
            alt="New York Times"
            className="px-1 sm:px-6 h-2 sm:h-3 object-contain flex-shrink-0"
            style={{ filter: logoFilter }}
          />
        </div>
        <div
          className="flex flex-col sm:flex-row w-full gap-3 sm:gap-4 items-start justify-start p-3 sm:p-5"
          style={{
            backgroundColor: styleLibrary?.colors?.neutrals?.light ?? "#FFFFFF",
          }}
        >
          <div className="flex flex-row sm:flex-row gap-2 sm:gap-3 items-start justify-start w-full sm:w-auto">
            <div className="flex flex-row sm:flex-col gap-2 sm:gap-3 overflow-x-auto sm:overflow-x-visible">
              <div
                className="w-8 h-8 sm:w-9 sm:h-9 border flex-shrink-0"
                style={{
                  backgroundColor:
                    styleLibrary?.colors?.neutrals?.subtle ?? "#F5F5F5",
                  borderColor:
                    styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                  ...(getImageAtIndex(1) && {
                    backgroundImage: `url(${getImageAtIndex(1)})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }),
                }}
              />
              <div
                className="w-8 h-8 sm:w-9 sm:h-9 border flex-shrink-0"
                style={{
                  backgroundColor:
                    styleLibrary?.colors?.neutrals?.subtle ?? "#F5F5F5",
                  borderColor:
                    styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                  ...(getImageAtIndex(2) && {
                    backgroundImage: `url(${getImageAtIndex(2)})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }),
                }}
              />
              <div
                className="w-8 h-8 sm:w-9 sm:h-9 border flex-shrink-0"
                style={{
                  backgroundColor:
                    styleLibrary?.colors?.neutrals?.subtle ?? "#F5F5F5",
                  borderColor:
                    styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                  ...(getImageAtIndex(3) && {
                    backgroundImage: `url(${getImageAtIndex(3)})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }),
                }}
              />
              <div
                className="w-8 h-8 sm:w-9 sm:h-9 border flex-shrink-0"
                style={{
                  backgroundColor:
                    styleLibrary?.colors?.neutrals?.subtle ?? "#F5F5F5",
                  borderColor:
                    styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                  ...(getImageAtIndex(4) && {
                    backgroundImage: `url(${getImageAtIndex(4)})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }),
                }}
              />
            </div>
            <div
              className="w-24 h-24 sm:w-40 md:w-50 sm:h-40 md:h-50 border flex-shrink-0"
              style={{
                backgroundColor:
                  styleLibrary?.colors?.neutrals?.subtle ?? "#F5F5F5",
                borderColor: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                ...(getImageAtIndex(5) && {
                  backgroundImage: `url(${getImageAtIndex(5)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }),
              }}
            />
          </div>
          <div className="flex flex-col gap-2 items-start justify-start flex-1 min-w-0">
            <div className="flex flex-col gap-1">
              <div className="flex flex-col sm:flex-row gap-1">
                <div
                  className="font-bold"
                  style={{
                    color:
                      styleLibrary?.colors?.brand?.primary?.color ?? "#000000",
                    fontFamily: `'${bodyFont}', sans-serif`,
                    fontSize: "8px",
                  }}
                >
                  ★★★★★
                </div>
                <div
                  className="font-light"
                  style={{
                    color: styleLibrary?.colors?.neutrals?.dark ?? "#999999",
                    fontFamily: `'${bodyFont}', sans-serif`,
                    fontSize: "8px",
                  }}
                >
                  4.8 88,888+ Trusted Customer Reviews
                </div>
              </div>
              <h1
                className="text-lg sm:text-xl font-bold"
                style={{
                  color: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                  fontFamily: `'${headingFont}', sans-serif`,
                }}
              >
                Product Title
              </h1>
              <div
                className="font-light"
                style={{
                  color: styleLibrary?.colors?.neutrals?.dark ?? "#999999",
                  fontFamily: `'${bodyFont}', sans-serif`,
                  fontSize: "8px",
                }}
              >
                This is a short description that describes your product.
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div
                className="font-medium"
                style={{
                  color: styleLibrary?.colors?.neutrals?.dark ?? "#999999",
                  fontFamily: `'${bodyFont}', sans-serif`,
                  fontSize: "8px",
                }}
              >
                Purchase Option
              </div>
              <div
                className="flex flex-col p-2 gap-1 rounded-sm border"
                style={{
                  backgroundColor:
                    styleLibrary?.colors?.brand?.secondary?.color ?? "#000000",
                  borderColor:
                    styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                }}
              >
                <div className="flex flex-row justify-between items-center">
                  <div className="flex flex-row items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full border flex items-center justify-center"
                      style={{
                        backgroundColor:
                          styleLibrary?.colors?.neutrals?.light ?? "#FFFFFF",
                        borderColor:
                          styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                      }}
                    >
                      <div
                        className="w-1 h-1 rounded-full"
                        style={{
                          backgroundColor:
                            styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                        }}
                      />
                    </div>
                    <div
                      className="font-medium"
                      style={{
                        color: getContrastingTextColor(
                          styleLibrary?.colors?.brand?.secondary?.color ??
                            "#000000",
                        ),
                        fontFamily: `'${bodyFont}', sans-serif`,
                        fontSize: "8px",
                      }}
                    >
                      Subscribe + Save 30%
                    </div>
                  </div>
                  <div
                    className="font-bold"
                    style={{
                      color: getContrastingTextColor(
                        styleLibrary?.colors?.brand?.secondary?.color ??
                          "#000000",
                      ),
                      fontFamily: `'${bodyFont}', sans-serif`,
                      fontSize: "8px",
                    }}
                  >
                    $76.28
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex flex-row items-center gap-2">
                    <div
                      className="font-bold"
                      style={{
                        color: getContrastingTextColor(
                          styleLibrary?.colors?.brand?.secondary?.color ??
                            "#000000",
                        ),
                        fontFamily: `'${bodyFont}', sans-serif`,
                        fontSize: "8px",
                      }}
                    >
                      ✓
                    </div>
                    <div
                      className="font-normal"
                      style={{
                        color: getContrastingTextColor(
                          styleLibrary?.colors?.brand?.secondary?.color ??
                            "#000000",
                        ),
                        fontFamily: `'${bodyFont}', sans-serif`,
                        fontSize: "8px",
                      }}
                    >
                      Save 30% on your first order
                    </div>
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    <div
                      className="font-bold"
                      style={{
                        color: getContrastingTextColor(
                          styleLibrary?.colors?.brand?.secondary?.color ??
                            "#000000",
                        ),
                        fontFamily: `'${bodyFont}', sans-serif`,
                        fontSize: "8px",
                      }}
                    >
                      ✓
                    </div>
                    <div
                      className="font-normal"
                      style={{
                        color: getContrastingTextColor(
                          styleLibrary?.colors?.brand?.secondary?.color ??
                            "#000000",
                        ),
                        fontFamily: `'${bodyFont}', sans-serif`,
                        fontSize: "8px",
                      }}
                    >
                      Shipped monthly - pause, skip, or cancel at any time for
                      any reason
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="flex flex-col p-2 gap-1 rounded-sm border"
                style={{
                  backgroundColor:
                    styleLibrary?.colors?.neutrals?.light ?? "#000000",
                  borderColor:
                    styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                }}
              >
                <div className="flex flex-row justify-between items-center">
                  <div className="flex flex-row items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full border flex items-center justify-center"
                      style={{
                        backgroundColor:
                          styleLibrary?.colors?.neutrals?.light ?? "#FFFFFF",
                        borderColor:
                          styleLibrary?.colors?.neutrals?.muted ?? "#000000",
                      }}
                    >
                      <div
                        className="w-1 h-1 rounded-full"
                        style={{
                          backgroundColor:
                            styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                        }}
                      />
                    </div>
                    <div
                      className="font-medium"
                      style={{
                        color: getContrastingTextColor(
                          styleLibrary?.colors?.neutrals?.light ?? "#FFFFFF",
                        ),
                        fontFamily: `'${bodyFont}', sans-serif`,
                        fontSize: "8px",
                      }}
                    >
                      One-Time Purchase
                    </div>
                  </div>
                  <div
                    className="font-bold"
                    style={{
                      color: getContrastingTextColor(
                        styleLibrary?.colors?.neutrals?.light ?? "#FFFFFF",
                      ),
                      fontFamily: `'${bodyFont}', sans-serif`,
                      fontSize: "8px",
                    }}
                  >
                    $109.99
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div
                className="font-medium"
                style={{
                  color: styleLibrary?.colors?.neutrals?.dark ?? "#999999",
                  fontFamily: `'${bodyFont}', sans-serif`,
                  fontSize: "8px",
                }}
              >
                Variant
              </div>
              <div className="flex flex-row gap-2 sm:gap-4 overflow-x-auto">
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border bg-cover bg-center"
                    style={{
                      backgroundColor:
                        styleLibrary?.colors?.neutrals?.light ?? "#FFFFFF",
                      borderColor:
                        styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                      backgroundImage: "url(/images/onboarding/variant.svg)",
                    }}
                  />
                  <div
                    className="font-normal text-center whitespace-nowrap"
                    style={{
                      color: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                      fontFamily: `'${bodyFont}', sans-serif`,
                      fontSize: "6px",
                    }}
                  >
                    Unflavored
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-cover bg-center"
                    style={{
                      backgroundColor:
                        styleLibrary?.colors?.neutrals?.muted ?? "#CCCCCC",
                      backgroundImage: "url(/images/onboarding/variant.svg)",
                    }}
                  />
                  <div
                    className="font-normal text-center whitespace-nowrap"
                    style={{
                      color: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                      fontFamily: `'${bodyFont}', sans-serif`,
                      fontSize: "6px",
                    }}
                  >
                    Orange
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-cover bg-center"
                    style={{
                      backgroundColor:
                        styleLibrary?.colors?.neutrals?.muted ?? "#CCCCCC",
                      backgroundImage: "url(/images/onboarding/variant.svg)",
                    }}
                  />
                  <div
                    className="font-normal text-center whitespace-nowrap"
                    style={{
                      color: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                      fontFamily: `'${bodyFont}', sans-serif`,
                      fontSize: "6px",
                    }}
                  >
                    Peach
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-cover bg-center"
                    style={{
                      backgroundColor:
                        styleLibrary?.colors?.neutrals?.muted ?? "#CCCCCC",
                      backgroundImage: "url(/images/onboarding/variant.svg)",
                    }}
                  />
                  <div
                    className="font-normal text-center whitespace-nowrap"
                    style={{
                      color: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                      fontFamily: `'${bodyFont}', sans-serif`,
                      fontSize: "6px",
                    }}
                  >
                    Watermelon
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-cover bg-center"
                    style={{
                      backgroundColor:
                        styleLibrary?.colors?.neutrals?.muted ?? "#CCCCCC",
                      backgroundImage: "url(/images/onboarding/variant.svg)",
                    }}
                  />
                  <div
                    className="font-normal text-center whitespace-nowrap"
                    style={{
                      color: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                      fontFamily: `'${bodyFont}', sans-serif`,
                      fontSize: "6px",
                    }}
                  >
                    Pineapple
                  </div>
                </div>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              UNSAFE_className="rounded-none w-full h-[25px] font-light text-xs"
              style={{
                backgroundColor:
                  styleLibrary?.colors?.brand?.primary?.color ?? "#000000",
                color: getContrastingTextColor(
                  styleLibrary?.colors?.brand?.primary?.color ?? "#000000",
                ),
              }}
            >
              Add to Cart
            </Button>
          </div>
        </div>
        <div
          className="flex flex-col h-full w-full items-center gap-2 p-2 sm:p-4"
          style={{
            backgroundColor:
              styleLibrary?.colors?.neutrals?.subtle ?? "#F5F5F5",
          }}
        >
          <div className="flex flex-col w-full pb-2 gap-1 items-center">
            <h1
              className="text-lg sm:text-xl font-bold text-center"
              style={{
                color: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                fontFamily: `'${headingFont}', sans-serif`,
              }}
            >
              Why You'll <span className="italic">Love</span> Us
            </h1>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full">
            <div
              className="flex-1 h-24 sm:h-28 md:h-35 border"
              style={{
                backgroundColor:
                  styleLibrary?.colors?.neutrals?.light ?? "#FFFFFF",
                borderColor: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                ...(getImageAtIndex(6) && {
                  backgroundImage: `url(${getImageAtIndex(6)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }),
              }}
            />
            <div
              className="flex-1 h-24 sm:h-28 md:h-35 border"
              style={{
                backgroundColor:
                  styleLibrary?.colors?.neutrals?.light ?? "#FFFFFF",
                borderColor: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                ...(getImageAtIndex(7) && {
                  backgroundImage: `url(${getImageAtIndex(7)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }),
              }}
            />
            <div
              className="flex-1 h-24 sm:h-28 md:h-35 border"
              style={{
                backgroundColor:
                  styleLibrary?.colors?.neutrals?.light ?? "#FFFFFF",
                borderColor: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
                ...(getImageAtIndex(8) && {
                  backgroundImage: `url(${getImageAtIndex(8)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTemplate;
